/**
 * Eventos de cobrança de uma fatura — protesto, Serasa, notificação, pesquisa de bens,
 * sub-rogação.
 *
 * Cada linha é uma ação com PRAZO e DONO: nasce pendente e depois alguém a conclui ou cancela.
 * A tela é a fila de trabalho de quem cobra, e é por isso que o prazo vencido fica vermelho —
 * uma pendência que não chama atenção é uma pendência esquecida.
 *
 * ⚠️ Não confundir com o histórico de `billing_action`, que é o log do ATO DE PAGAR.
 */
import { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaCommentDots, FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import RefreshButton from '../../../atoms/RefreshButton';
import BadgeStatus from '../../../atoms/BadgeStatus';
import CommentCell from '../../../molecules/CommentCell';
import {
    BillingEventStatus, InvoiceBillingEventRow, listInvoiceBillingEvents,
    updateInvoiceBillingEvent,
} from '../services/billingApi';
import { formatDateBR, formatDateTimeBR, todayISO } from '../../../../utils/DateUtils';
import PaymentDateInput from '../../../molecules/payment/PaymentDateInput';
import AddBillingEventCommentModal from './AddBillingEventCommentModal';
import NewInvoiceBillingEventModal from './NewInvoiceBillingEventModal';
import { InstantDraft, draftInstant, nowDraft, touchDraft } from './completionInstant';

const TH = 'px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300';
const TD = 'px-3 py-2 text-sm align-top';

/**
 * Célula de texto livre que CORTA em vez de crescer.
 *
 * ⚠️ O `truncate` precisa do `div` com teto: num `<td>` de tabela `table-auto` ele não tem efeito
 * — a célula se alarga para caber o conteúdo e é a TABELA que estoura. É o mesmo arranjo do
 * `PartyCell` e da coluna de Comentários.
 *
 * `title` com o texto inteiro: cortar sem deixar como ler o resto troca um problema por outro.
 */
const TruncatedCell: React.FC<{ text: string; maxWidthClass?: string }> = ({
    text, maxWidthClass = 'max-w-[200px]',
}) => <div className={`truncate ${maxWidthClass}`} title={text}>{text}</div>;

/**
 * Base dos ícones de ação da linha. A COR vem por fora (verde/vermelho/azul), e é aplicada no
 * estado normal — não só no hover.
 *
 * `disabled:text-gray-300` sobrepõe a cor: é o cinza que diz "não dá", e ele precisa ganhar do
 * verde/vermelho da classe base. Sem `disabled:hover:` explícito, o hover do Tailwind ainda
 * pintaria um botão desabilitado.
 */
const ACTION_BUTTON = 'align-middle disabled:text-gray-300 dark:disabled:text-slate-600 '
    + 'disabled:cursor-not-allowed disabled:hover:text-gray-300 dark:disabled:hover:text-slate-600';

type BadgeColor = 'pending' | 'green' | 'red' | 'gray';

/**
 * Amarelo/verde/vermelho como pedido na spec.
 *
 * Aqui o amarelo não disputa com nada: diferente da tela de Faturas — onde o OPEN saiu de
 * `yellow` para `gold` justamente para não competir com o vermelho do vencido —, o vermelho
 * desta tabela mora na COLUNA DE PRAZO, não no selo.
 *
 * `pending` é uma chave própria do BadgeStatus (bg-yellow-400), e não o `yellow` (bg-yellow-500):
 * aquele é usado por Operações, structuring e settlement, e repintá-lo mudaria três telas.
 */
export const STATUS_META: Record<string, { label: string; color: BadgeColor }> = {
    PENDING: { label: 'Pendente', color: 'pending' },
    COMPLETED: { label: 'Concluído', color: 'green' },
    CANCELLED: { label: 'Cancelado', color: 'red' },
};

/** Nome quando houver, usuário quando não — nunca célula vazia com dado no banco.
 *
 *  O `current_user()` do backend devolve o display name do authorizer, que PODE vir nulo. Cair
 *  para o username é pior do que o nome e muito melhor do que um traço: saber quem agiu é o
 *  motivo da coluna existir. */
export const personLabel = (name: string | null, user: string | null): string =>
    name || user || '—';

/** Prazo estourado: só faz sentido em pendência — concluído e cancelado não devem mais nada.
 *
 *  Compara com `todayISO()` (componentes LOCAIS) e não com `toISOString()`, que converte para UTC
 *  e devolveria ontem depois das 21h no Brasil — justamente quando alguém olha o que venceu. */
export const isOverdue = (row: InvoiceBillingEventRow): boolean =>
    row.status === 'PENDING' && !!row.eventDateLimit && row.eventDateLimit < todayISO();

interface PanelProps {
    invoiceRef: string;
    onChanged?: () => void;
    hideAddButton?: boolean;
    title?: string;
    reloadKey?: number;
    zIndexClass?: string;
    confirmZIndexClass?: string;
}

const InvoiceBillingEventsPanel: React.FC<PanelProps> = ({
    invoiceRef, onChanged, hideAddButton = false, title, reloadKey = 0, zIndexClass = 'z-[60]', confirmZIndexClass = 'z-[70]',
}) => {
    const [rows, setRows] = useState<InvoiceBillingEventRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [adding, setAdding] = useState(false);
    /** linha que está recebendo comentário — `null` = nenhum diálogo aberto */
    const [commenting, setCommenting] = useState<InvoiceBillingEventRow | null>(null);
    /** id da linha em trânsito: desabilita os botões DAQUELA linha, não da tabela toda */
    const [saving, setSaving] = useState<number | null>(null);
    /**
     * Desfecho aguardando confirmação — `null` = nenhum diálogo aberto.
     *
     * Concluir e cancelar são TERMINAIS: o backend recusa um segundo desfecho, então o clique não
     * tem volta. Um clique só resolvendo a ação é fácil de dar por engano na linha errada de uma
     * tabela com nove colunas.
     */
    const [confirming, setConfirming] = useState<
        { row: InvoiceBillingEventRow; status: BillingEventStatus } | null>(null);
    /**
     * Quando a conclusão aconteceu. Pré-carregado com agora; intocado, quem carimba é o backend.
     *
     * Só a conclusão tem data — cancelar é o registro de que a ação NÃO vai acontecer, e datar
     * isso não diz nada a ninguém.
     */
    const [completion, setCompletion] = useState<InstantDraft>(nowDraft);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setRows(await listInvoiceBillingEvents(invoiceRef));
        } catch (e: any) {
            setError(e?.message || 'Falha ao carregar os eventos de cobrança');
        } finally {
            setLoading(false);
        }
    }, [invoiceRef]);

    useEffect(() => { load(); }, [load, reloadKey]);

    /** Recarrega em vez de aplicar o retorno na linha: `completedAt` e o texto acumulado são
     *  montados pelo backend, e remontá-los aqui duplicaria a regra. */
    const afterChange = async () => {
        await load();
        onChanged?.();
    };

    const confirmFinish = async () => {
        if (!confirming) return;
        const { row, status } = confirming;
        setConfirming(null);
        setSaving(row.id);
        try {
            await updateInvoiceBillingEvent(invoiceRef, row.id, {
                status,
                // só a conclusão leva instante, e só se alguém mexeu no campo
                completed_at: status === 'COMPLETED' ? draftInstant(completion) : undefined,
            });
            toast.success(status === 'COMPLETED' ? 'Evento concluído.' : 'Evento cancelado.');
            await afterChange();
        } catch (e: any) {
            toast.error(e?.message || 'Não foi possível atualizar o evento');
        } finally {
            setSaving(null);
        }
    };

    return (
        <>
            {title && (
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
                    <RefreshButton variant="icon" label="os eventos de cobrança" busy={loading} onClick={load} />
                </div>
            )}
            {loading && <p className="text-sm text-gray-500 py-6 text-center">Carregando eventos...</p>}

            {!loading && error && (
                <div className="py-6 text-center">
                    <p className="text-sm text-red-600">{error}</p>
                    <button onClick={load} className="mt-2 text-sm text-blue-600 hover:underline">
                        Tentar de novo
                    </button>
                </div>
            )}

            {!loading && !error && (
                <>
                    {!(title && hideAddButton) && (
                        <div className={`flex items-center mb-1 ${hideAddButton ? 'justify-end' : 'justify-between'}`}>
                            {!hideAddButton && (
                                <button onClick={() => setAdding(true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                                    <FaPlus size={11} /> Adicionar
                                </button>
                            )}
                            {!title && <RefreshButton label="os eventos de cobrança" busy={loading} onClick={load} />}
                        </div>
                    )}

                    {rows.length === 0 && (
                        <div className="py-8 text-center">
                            <p className="text-sm text-gray-500">
                                Nenhum evento de cobrança nesta fatura.
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                Use "Adicionar" para registrar protesto, Serasa, notificação e afins.
                            </p>
                        </div>
                    )}

                    {rows.length > 0 && (
                        <div className="border dark:border-slate-600 rounded overflow-hidden mt-2">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-slate-700">
                                    <tr>
                                        <th className={TH}>Status</th>
                                        <th className={TH}>Ação</th>
                                        <th className={TH}>Quem Incluiu?</th>
                                        <th className={`${TH} whitespace-nowrap`}>Data Inclusão</th>
                                        <th className={TH}>Comentários</th>
                                        <th className={`${TH} whitespace-nowrap`}>Data Conclusão</th>
                                        <th className={`${TH} whitespace-nowrap`}>Data Limite da Ação</th>
                                        <th className={TH}>Executado Por</th>
                                        <th className={`${TH} text-right`}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => {
                                        const meta = STATUS_META[row.status]
                                            || { label: row.status, color: 'gray' as BadgeColor };
                                        const pending = row.status === 'PENDING';
                                        const busy = saving === row.id;
                                        const blocked = !pending
                                            ? `Evento já ${meta.label.toLowerCase()}`
                                            : undefined;
                                        return (
                                            <tr key={row.id} className="border-t dark:border-slate-600">
                                                <td className={TD}>
                                                    <BadgeStatus text={meta.label} color={meta.color} />
                                                </td>
                                                <td className={TD}>
                                                    <TruncatedCell
                                                        text={row.billingEventName || row.billingEventRef}
                                                        maxWidthClass="max-w-[240px]" />
                                                </td>
                                                <td className={TD}>
                                                    <TruncatedCell
                                                        text={personLabel(row.createdByName, row.createdBy)}
                                                        maxWidthClass="max-w-[160px]" />
                                                </td>
                                                <td className={`${TD} whitespace-nowrap`}>{formatDateTimeBR(row.createdAt)}</td>
                                                {/* o CommentCell já corta quando há
                                                    vários; o teto aqui é para o
                                                    comentário ÚNICO, que quebra linha */}
                                                <td className={`${TD} max-w-[260px]`}>
                                                    <CommentCell notes={row.notesList} />
                                                </td>
                                                <td className={`${TD} whitespace-nowrap`}>{formatDateTimeBR(row.completedAt)}</td>
                                                {/* vermelho na DATA e não no selo: o status continua
                                                    dizendo "pendente", e o que está errado é o prazo */}
                                                <td className={`${TD} whitespace-nowrap ${isOverdue(row) ? 'text-red-600 font-medium' : ''}`}>
                                                    {formatDateBR(row.eventDateLimit)}
                                                </td>
                                                <td className={TD}>
                                                    <TruncatedCell
                                                        text={personLabel(row.finishedByName, row.finishedBy)}
                                                        maxWidthClass="max-w-[160px]" />
                                                </td>
                                                <td className={`${TD} text-right whitespace-nowrap`}>
                                                    {/* Ícone COLORIDO já no estado ativo. Antes ficava
                                                        cinza e só se pintava no hover, o que fazia um
                                                        botão disponível parecer desabilitado — e num
                                                        lugar onde alguns REALMENTE são, a diferença
                                                        precisa se ver sem passar o mouse. */}
                                                    <button onClick={() => setCommenting(row)}
                                                        aria-label={`Adicionar comentário ao evento ${row.billingEventName || row.billingEventRef}`}
                                                        title="Adicionar comentário"
                                                        className={`${ACTION_BUTTON} text-blue-600 hover:text-blue-700 mr-2`}>
                                                        <FaCommentDots />
                                                    </button>
                                                    {/* `<span title>` em volta: um <button disabled>
                                                        engole o tooltip nativo em alguns navegadores, e
                                                        é justamente no desabilitado que o motivo importa */}
                                                    <span title={blocked || 'Marcar como concluído'}>
                                                        <button onClick={() => { setCompletion(nowDraft()); setConfirming({ row, status: 'COMPLETED' }); }}
                                                            disabled={!pending || busy}
                                                            aria-label={`Concluir evento ${row.billingEventName || row.billingEventRef}`}
                                                            className={`${ACTION_BUTTON} text-green-600 hover:text-green-700 mr-2`}>
                                                            <FaCheck />
                                                        </button>
                                                    </span>
                                                    <span title={blocked || 'Marcar como cancelado'}>
                                                        <button onClick={() => setConfirming({ row, status: 'CANCELLED' })}
                                                            disabled={!pending || busy}
                                                            aria-label={`Cancelar evento ${row.billingEventName || row.billingEventRef}`}
                                                            className={`${ACTION_BUTTON} text-red-600 hover:text-red-700`}>
                                                            <FaTimes />
                                                        </button>
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
            {confirming && (
                <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${confirmZIndexClass} p-4`}>
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                        <h3 className="text-base font-semibold mb-2">
                            {confirming.status === 'COMPLETED'
                                ? 'Marcar este evento como concluído?'
                                : 'Marcar este evento como cancelado?'}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            <strong className="break-words">
                                {confirming.row.billingEventName || confirming.row.billingEventRef}
                            </strong>
                        </p>
                        {confirming.status === 'COMPLETED' && (
                            <div className="mt-4">
                                <PaymentDateInput
                                    label="Data Conclusão"
                                    date={completion.date} time={completion.time}
                                    showTime={completion.showTime}
                                    onChange={next => setCompletion(touchDraft(completion, next))} />
                                <p className="text-xs text-gray-500 mt-1">
                                    Deixe como está para registrar o momento atual.
                                </p>
                            </div>
                        )}

                        {/* o porquê da confirmação existir, dito na hora da decisão: o backend
                            recusa um segundo desfecho, então isto não tem desfazer */}
                        <p className="text-xs text-gray-500 mt-2">
                            O desfecho é definitivo — depois de concluído ou cancelado, o evento não
                            volta a ficar pendente. Comentários continuam podendo ser adicionados.
                        </p>

                        <div className="flex flex-wrap gap-2 justify-end mt-5">
                            <button onClick={() => setConfirming(null)}
                                className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                                Voltar
                            </button>
                            <button onClick={confirmFinish}
                                className={`px-4 py-2 text-sm rounded text-white ${confirming.status === 'COMPLETED'
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-red-600 hover:bg-red-700'}`}>
                                {confirming.status === 'COMPLETED' ? 'Concluir' : 'Cancelar evento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* z-[60]: acima deste modal (z-50). Por baixo, o usuário só veria a tela escurecer */}
            {adding && (
                <NewInvoiceBillingEventModal
                    invoiceRef={invoiceRef}
                    onClose={() => setAdding(false)}
                    onCreated={async () => { setAdding(false); await afterChange(); }}
                    zIndexClass={zIndexClass} />
            )}
            {commenting && (
                <AddBillingEventCommentModal
                    invoiceRef={invoiceRef}
                    event={commenting}
                    onClose={() => setCommenting(null)}
                    onAdded={async () => { setCommenting(null); await afterChange(); }}
                    zIndexClass={zIndexClass} />
            )}
        </>
    );
};

export default InvoiceBillingEventsPanel;
